import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { injectBusinessSchema, injectBusinessMeta, removeJsonLd, slugify } from "../lib/seo";
import MapView from "../components/MapView";
import ShareButton from "../components/ShareButton";
import PremiumComparator from "../components/PremiumComparator";
import LaunchOfferBanner from "../components/LaunchOfferBanner";
import { HoursDisplay } from "../components/HoursEditor";
import { Phone, WhatsappLogo, Globe, MapPin, Star, EnvelopeSimple, ArrowLeft, Crown, Eye, GoogleLogo, Receipt, ShieldSlash } from "@phosphor-icons/react";
import QuoteRequestModal from "../components/QuoteRequestModal";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { toast } from "sonner";

export default function BusinessDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const [biz, setBiz] = useState(null);
  const [liveStats, setLiveStats] = useState(null);
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [reviewForm, setReviewForm] = useState({ author_name: "", author_email: "", rating: 5, text: "" });
  const [sending, setSending] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const [relatedCity, setRelatedCity] = useState([]);
  const [relatedCategory, setRelatedCategory] = useState([]);
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    api.get(`/public/businesses/${slug}`).then((r) => {
      // VAGUE 33C — Old-slug redirect: if the API returns a canonical_slug
      // different from the URL param, replace the URL client-side so the
      // browser bar + shares always use the new canonical slug.
      if (r.data?.canonical_slug && r.data.canonical_slug !== slug) {
        navigate(`/entreprise/${r.data.canonical_slug}`, { replace: true });
        return;
      }
      setBiz(r.data);
    }).catch(() => setBiz(false));
    api.get(`/public/businesses/${slug}/live-stats`).then((r) => setLiveStats(r.data)).catch(() => {});
    const itv = setInterval(() => {
      api.get(`/public/businesses/${slug}/live-stats`).then((r) => setLiveStats(r.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(itv);
  }, [slug, navigate]);

  // Inject JSON-LD LocalBusiness + Breadcrumb + full meta for SEO
  useEffect(() => {
    if (!biz) return;
    injectBusinessMeta(biz);
    injectBusinessSchema(biz);
    // Fetch related businesses for internal linking (strong SEO signal)
    if (biz.city) {
      api.get(`/public/businesses?city=${encodeURIComponent(biz.city)}&limit=8`)
        .then((r) => setRelatedCity((r.data?.items || []).filter((b) => b.slug !== biz.slug).slice(0, 6)))
        .catch(() => {});
    }
    if (biz.category) {
      api.get(`/public/businesses?category=${encodeURIComponent(biz.category)}&limit=8`)
        .then((r) => setRelatedCategory((r.data?.items || []).filter((b) => b.slug !== biz.slug).slice(0, 6)))
        .catch(() => {});
    }
    return () => {
      removeJsonLd("mc68-jsonld");
      removeJsonLd("mc68-breadcrumb");
    };
  }, [biz]);

  // VAGUE 41 — telepro share link /entreprise/:slug?ref=CODE → remember the code for signup attribution
  useEffect(() => {
    const ref = (searchParams.get("ref") || "").trim().toUpperCase();
    if (ref) { try { localStorage.setItem("mc68_telepro_ref", ref); } catch { /* noop */ } }
  }, [searchParams]);

  // Auto-claim after magic-link return if ?claim=1 and user is logged in
  useEffect(() => {
    if (!user || !biz || biz === false) return;
    if (searchParams.get("claim") !== "1") return;
    if (biz.claimed_by === user.id) {
      searchParams.delete("claim");
      setSearchParams(searchParams, { replace: true });
      toast.success("Vous êtes déjà propriétaire de cette fiche.");
      setTimeout(() => { window.location.href = `/dashboard?slug=${slug}`; }, 1200);
      return;
    }
    (async () => {
      try {
        const referral = localStorage.getItem("mc68_referral") || undefined;
        await api.post(`/dashboard/claim/${slug}`, referral ? { referral_code: referral } : {});
        if (referral) {
          localStorage.removeItem("mc68_referral");
          toast.success("Fiche réclamée avec parrainage appliqué ! Redirection…");
        } else {
          toast.success("Fiche réclamée ! Redirection vers votre espace partenaire…");
        }
        searchParams.delete("claim");
        setSearchParams(searchParams, { replace: true });
        setTimeout(() => { window.location.href = `/dashboard?slug=${slug}`; }, 1500);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur lors de la réclamation");
        searchParams.delete("claim");
        setSearchParams(searchParams, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, biz]);

  if (biz === false) return <div className="mx-auto max-w-3xl p-12 text-center" data-testid="business-404">Entreprise introuvable. <Link to="/annuaire" className="text-primary underline">Retour à l'annuaire</Link></div>;
  if (!biz) return <div className="p-20 text-center text-muted-foreground">Chargement…</div>;

  const isP = biz.premium && (biz.premium.tier === "premium" || biz.premium.tier === "premium_ai");
  const isPremiumAI = biz.premium && biz.premium.tier === "premium_ai";
  const isOwner = user && biz.claimed_by && biz.claimed_by === user.id;
  const ownerNudge = isOwner && !isP && liveStats && liveStats.intents_week > 0;

  const claim = async () => {
    if (!user) {
      let ref = "";
      try { ref = localStorage.getItem("mc68_telepro_ref") || ""; } catch { /* noop */ }
      return navigate(`/inscription?claim=${encodeURIComponent(slug)}${ref ? `&ref=${encodeURIComponent(ref)}` : ""}`);
    }
    try {
      await api.post(`/dashboard/claim/${slug}`);
      toast.success("Fiche réclamée. Rendez-vous dans votre espace partenaire.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const sendContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/public/businesses/${slug}/contact`, { ...contactForm, business_id: biz.id, language: "fr" });
      toast.success(t.business.sent);
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Erreur d'envoi");
    }
    setSending(false);
  };

  const sendReview = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post(`/public/businesses/${slug}/reviews`, reviewForm);
      toast.success(r.data.moderation || "Avis envoyé");
      setReviewForm({ author_name: "", author_email: "", rating: 5, text: "" });
    } catch {
      toast.error("Erreur");
    }
  };

  const trackClick = (kind) => api.post(`/public/click/${slug}/${kind}`).catch(() => {});

  // Google Maps deep-link — open the business' reviews directly. Uses search query
  // (name + city) which Google resolves to the right Place. No API key required.
  const googleReviewsUrl = biz.name && biz.city ? (() => {
    const q = encodeURIComponent(`${biz.name} ${biz.city} avis`);
    return `https://www.google.com/maps/search/?api=1&query=${q}`;
  })() : null;

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-12" data-testid="business-detail-page">
      {ownerNudge && (
        <div
          className="mb-6 rounded-sm bg-gradient-to-r from-secondary to-[#d68b6c] text-secondary-foreground px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 shadow-sm"
          data-testid="owner-nudge-banner"
        >
          <div className="text-2xl">👀</div>
          <div className="flex-1">
            <div className="font-display text-lg font-medium leading-tight">
              {liveStats.intents_week} personne{liveStats.intents_week > 1 ? "s" : ""} ont cherché à vous joindre cette semaine
            </div>
            <div className="text-sm opacity-90 mt-0.5">
              Votre téléphone et votre email sont encore masqués aux visiteurs. Activez votre essai gratuit pour ne plus rater ces contacts.
            </div>
          </div>
          <Link
            to="/passez-partenaire"
            className="shrink-0 inline-flex items-center gap-1.5 bg-white text-secondary hover:bg-[#F9F8F6] px-4 py-2.5 rounded-sm text-sm font-semibold transition-colors"
            data-testid="owner-nudge-cta"
          >
            Activer mon essai 14 jours
          </Link>
        </div>
      )}
      {/* Visible breadcrumbs — SEO + UX */}
      <nav aria-label="Fil d'ariane" className="text-xs text-muted-foreground mb-6 flex flex-wrap items-center gap-1" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-foreground">Accueil</Link>
        <span>›</span>
        <Link to="/annuaire" className="hover:text-foreground">Annuaire</Link>
        {biz.city && (<>
          <span>›</span>
          <Link to={`/villes/${slugify(biz.city)}`} className="hover:text-foreground">{biz.city}</Link>
        </>)}
        {biz.category && (<>
          <span>›</span>
          <Link to={`/categories/${slugify(biz.category)}`} className="hover:text-foreground capitalize">{biz.category}</Link>
        </>)}
        <span>›</span>
        <span className="text-foreground font-semibold truncate max-w-[240px]" title={biz.name}>{biz.name}</span>
      </nav>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          <div>
            {isP && <div className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-sm mb-4" data-testid="premium-badge"><Crown size={12} weight="fill"/> {t.directory.premium_badge}</div>}
            {biz.dnc && (
              <div
                className="inline-flex items-center gap-1.5 bg-muted text-muted-foreground border border-border text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 rounded-sm mb-4 ml-2"
                data-testid="dnc-badge"
                title="Ce commerce nous a demandé de ne plus être contacté pour du démarchage commercial. Nous respectons son choix."
              >
                <ShieldSlash size={12} weight="fill"/> Fermé au démarchage
              </div>
            )}
            <div className="label-uppercase mb-2">{biz.category}</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium mb-3">{biz.name}</h1>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1"><MapPin size={14}/> {biz.address || biz.city}, {biz.postal_code} {biz.city}</span>
                {(() => {
                  const cnt = biz.rating_count ?? biz.reviews_count ?? 0;
                  const avg = biz.rating_avg ?? biz.avg_rating ?? biz.rating;
                  if (!cnt || avg == null) return null;
                  return (
                    <span className="inline-flex items-center gap-1" data-testid="biz-rating">
                      <Star size={14} weight="fill" className="text-secondary"/>
                      {Number(avg).toFixed(1)} · {cnt} avis
                    </span>
                  );
                })()}
                {liveStats && (liveStats.total > 0 || liveStats.today > 0) && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 bg-primary/10 hover:bg-primary/15 text-primary font-semibold px-2.5 py-1 rounded-sm text-xs transition-colors cursor-pointer border border-primary/20 hover:border-primary/40"
                        data-testid="live-views-chip"
                        title="Cliquez pour voir le détail des vues"
                      >
                        {liveStats.today > 0 && (
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-75 animate-ping" />
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary" />
                          </span>
                        )}
                        <Eye size={13} weight="bold"/>
                        <span>Vu {(liveStats.total || liveStats.week || liveStats.today).toLocaleString("fr-FR")}&nbsp;fois</span>
                        <span className="opacity-50 ml-0.5">›</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-72 p-0 overflow-hidden" data-testid="live-views-popover">
                      <div className="bg-primary text-primary-foreground px-4 py-3">
                        <div className="text-[10px] uppercase tracking-[0.15em] font-bold opacity-80">Audience de cette fiche</div>
                        <div className="font-display text-lg font-medium leading-tight mt-0.5 truncate">{biz.name}</div>
                      </div>
                      <div className="p-4 space-y-2.5">
                        <div className="flex items-baseline justify-between border-b border-border pb-2">
                          <span className="text-xs text-muted-foreground">Aujourd'hui</span>
                          <span className="font-display text-xl font-medium tabular-nums" data-testid="stats-today">
                            {(liveStats.today || 0).toLocaleString("fr-FR")}
                            <span className="text-xs text-muted-foreground font-normal ml-1">visiteurs</span>
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between border-b border-border pb-2">
                          <span className="text-xs text-muted-foreground">7 derniers jours</span>
                          <span className="font-display text-xl font-medium tabular-nums" data-testid="stats-week">
                            {(liveStats.week || 0).toLocaleString("fr-FR")}
                            <span className="text-xs text-muted-foreground font-normal ml-1">visiteurs</span>
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-muted-foreground">Depuis le lancement</span>
                          <span className="font-display text-xl font-medium tabular-nums text-primary" data-testid="stats-total">
                            {(liveStats.total || 0).toLocaleString("fr-FR")}
                            <span className="text-xs text-muted-foreground font-normal ml-1">vues</span>
                          </span>
                        </div>
                        {liveStats.intents_week > 0 && (
                          <div className="mt-2 pt-2 border-t border-border flex items-baseline justify-between">
                            <span className="text-xs text-muted-foreground">Contacts (7j)</span>
                            <span className="font-display text-base font-medium tabular-nums text-secondary" data-testid="stats-intents">
                              {liveStats.intents_week.toLocaleString("fr-FR")}
                              <span className="text-xs text-muted-foreground font-normal ml-1">intentions</span>
                            </span>
                          </div>
                        )}
                        {!isP && (
                          <div className="mt-3 pt-3 border-t border-border text-xs bg-secondary/5 -mx-4 -mb-4 px-4 py-3">
                            <div className="font-semibold text-foreground mb-1">Ces visiteurs pourraient devenir vos clients.</div>
                            <div className="text-muted-foreground leading-snug mb-2">Passez Premium pour afficher téléphone, WhatsApp, site web et recevoir les demandes de devis.</div>
                            <Link to="/tarifs" className="inline-flex items-center gap-1 font-semibold text-primary hover:text-secondary" data-testid="stats-premium-cta">
                              Activer l'essai gratuit 14 jours →
                            </Link>
                          </div>
                        )}
                        {isP && (
                          <div className="mt-3 pt-3 border-t border-border text-[11px] text-muted-foreground leading-snug">
                            Chiffres mis à jour toutes les 30 secondes. Les vues incluent les visites Google, direct et interne à Mon Commerce 68.
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              <ShareButton
                url={typeof window !== "undefined" ? window.location.href : ""}
                title={`${biz.name} — ${biz.city}`}
                text={`${biz.name}, ${biz.category} à ${biz.city}`}
              />
            </div>
          </div>

          {biz.description?.fr ? (
            <p className="text-base leading-relaxed text-foreground/80 max-w-2xl">{biz.description.fr}</p>
          ) : (
            // Auto-generated keyword-rich paragraph for SEO (Free businesses without owner description)
            <p className="text-base leading-relaxed text-foreground/80 max-w-2xl" data-testid="seo-auto-description">
              <b>{biz.name}</b> est un établissement de type <b>{biz.category?.toLowerCase() || "commerce"}</b> situé à <b>{biz.city}{biz.postal_code ? ` (${biz.postal_code})` : ""}</b>, dans le département du <b>Haut-Rhin (68)</b>, en région Grand Est. Retrouvez ci-dessous les coordonnées, l'adresse complète, la carte de localisation{biz.rating_count > 0 ? `, la note moyenne (${biz.rating_avg?.toFixed(1)}/5 sur ${biz.rating_count} avis)` : ""} et tous les avis clients de <b>{biz.name}</b>. Mon Commerce 68 recense plus de 30 000 entreprises du Haut-Rhin — l'annuaire de référence du 68.
            </p>
          )}

          {biz.lat && biz.lon && (
            <div className="border border-border rounded-sm overflow-hidden">
              <MapView businesses={[biz]} single height={380} />
            </div>
          )}

          <HoursDisplay hours={biz.hours} />

          {/* Reviews */}
          <section className="border-t border-border pt-8">
            <h2 className="font-display text-2xl font-medium mb-6">{t.business.reviews}</h2>
            {(!biz.reviews || biz.reviews.length === 0) ? (
              <p className="text-sm text-muted-foreground mb-6">{t.business.no_reviews}</p>
            ) : (
              <div className="space-y-4 mb-8">
                {biz.reviews.map((r) => (
                  <div key={r.id} className="border border-border rounded-sm p-5 bg-card" data-testid={`review-${r.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-sm">{r.author_name}</div>
                      <div className="flex items-center gap-0.5 text-secondary">
                        {Array.from({ length: 5 }).map((_, i) => <Star key={`star-${i}`} size={14} weight={i < r.rating ? "fill" : "regular"} />)}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{r.text}</p>
                    {r.reply && (
                      <div className="mt-3 pl-3 border-l-2 border-primary/40 text-sm text-foreground/80">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-primary mb-1">Réponse de {biz.name}</div>
                        {r.reply}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <form onSubmit={sendReview} className="border border-border rounded-sm p-5 bg-card grid gap-3" data-testid="review-form">
              <div className="font-display font-semibold text-base">{t.business.submit_review}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input required value={reviewForm.author_name} onChange={(e) => setReviewForm({ ...reviewForm, author_name: e.target.value })} placeholder={t.business.name} className="border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary" data-testid="review-name"/>
                <input type="email" value={reviewForm.author_email} onChange={(e) => setReviewForm({ ...reviewForm, author_email: e.target.value })} placeholder={t.business.email} className="border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary" data-testid="review-email"/>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Note :</span>
                {[1,2,3,4,5].map((n) => (
                  <button type="button" key={n} onClick={() => setReviewForm({ ...reviewForm, rating: n })} data-testid={`review-rating-${n}`}>
                    <Star size={20} weight={n <= reviewForm.rating ? "fill" : "regular"} className="text-secondary"/>
                  </button>
                ))}
              </div>
              <textarea required value={reviewForm.text} onChange={(e) => setReviewForm({ ...reviewForm, text: e.target.value })} placeholder={t.business.message} rows={3} className="border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary" data-testid="review-text"/>
              <button type="submit" className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-2.5 text-sm font-semibold rounded-sm w-fit" data-testid="review-submit">{t.business.submit_review}</button>
            </form>
          </section>

          {/* Related businesses — internal linking (MAJOR SEO SIGNAL) */}
          {(relatedCity.length > 0 || relatedCategory.length > 0) && (
            <section className="border-t border-border pt-8" data-testid="related-businesses">
              {relatedCategory.length > 0 && biz.category && (
                <div className="mb-8">
                  <h2 className="font-display text-xl font-medium mb-4">Autres <span className="lowercase">{biz.category}</span> dans le Haut-Rhin</h2>
                  <ul className="grid sm:grid-cols-2 gap-2">
                    {relatedCategory.map((b) => (
                      <li key={b.slug}>
                        <Link to={`/entreprise/${b.slug}`} className="block border border-border hover:border-primary rounded-sm p-3 text-sm transition-colors" data-testid={`related-cat-${b.slug}`}>
                          <div className="font-semibold truncate">{b.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{b.city}{b.postal_code ? ` · ${b.postal_code}` : ""}</div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link to={`/categories/${slugify(biz.category)}`} className="inline-block mt-3 text-xs font-semibold text-primary hover:text-secondary">Voir tous les {biz.category.toLowerCase()} du 68 →</Link>
                </div>
              )}
              {relatedCity.length > 0 && biz.city && (
                <div>
                  <h2 className="font-display text-xl font-medium mb-4">Autres commerces à {biz.city}</h2>
                  <ul className="grid sm:grid-cols-2 gap-2">
                    {relatedCity.map((b) => (
                      <li key={b.slug}>
                        <Link to={`/entreprise/${b.slug}`} className="block border border-border hover:border-primary rounded-sm p-3 text-sm transition-colors" data-testid={`related-city-${b.slug}`}>
                          <div className="font-semibold truncate">{b.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{b.category}</div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link to={`/villes/${slugify(biz.city)}`} className="inline-block mt-3 text-xs font-semibold text-primary hover:text-secondary">Voir tous les commerces à {biz.city} →</Link>
                </div>
              )}
            </section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          {isP ? (
            <div className="bg-card border border-border rounded-sm p-6 space-y-3">
              {biz.phone && (
                <a href={`tel:${biz.phone}`} onClick={() => trackClick("phone")} className="flex items-center justify-center gap-2 bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-3 rounded-sm font-semibold text-sm transition-colors w-full" data-testid="phone-cta">
                  <Phone weight="fill" size={16}/> {biz.phone}
                </a>
              )}
              {biz.whatsapp && (
                <a href={`https://wa.me/${biz.whatsapp.replace(/\D/g,"")}`} target="_blank" rel="noreferrer" onClick={() => trackClick("whatsapp")} className="flex items-center justify-center gap-2 bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-3 rounded-sm font-semibold text-sm transition-colors w-full" data-testid="whatsapp-cta">
                  <WhatsappLogo weight="fill" size={16}/> WhatsApp
                </a>
              )}
              {biz.website && (() => {
                const safeUrl = /^https?:\/\//i.test(biz.website) ? biz.website : `https://${biz.website.replace(/^\/+/, "")}`;
                return (
                  <a href={safeUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 border border-border hover:border-foreground px-4 py-3 rounded-sm font-semibold text-sm transition-colors w-full" data-testid="website-cta"><Globe size={16}/> {t.business.website}</a>
                );
              })()}
              {googleReviewsUrl && (
                <a
                  href={googleReviewsUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackClick("google_reviews")}
                  className="flex items-center justify-center gap-2 border border-border hover:border-[#4285F4] hover:text-[#4285F4] px-4 py-3 rounded-sm font-semibold text-sm transition-colors w-full"
                  data-testid="google-reviews-cta"
                >
                  <GoogleLogo size={16} weight="bold"/> Voir nos avis sur Google
                </a>
              )}
              {isPremiumAI && (
                <button
                  onClick={() => { trackClick("quote_request_open"); setQuoteOpen(true); }}
                  className="flex items-center justify-center gap-2 bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-3 rounded-sm font-semibold text-sm transition-colors w-full"
                  data-testid="quote-request-cta"
                >
                  <Receipt size={16} weight="bold"/> Demander un devis
                </button>
              )}
            </div>
          ) : (
            <div className="bg-primary text-primary-foreground rounded-sm p-6" data-testid="claim-hero-card">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 shrink-0 bg-white/20 rounded-sm grid place-items-center font-display font-bold text-lg">?</div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">C'est vous ?</div>
                  <div className="font-display text-xl font-medium">C'est votre entreprise</div>
                </div>
              </div>
              <p className="text-sm opacity-90 mb-5 leading-relaxed">
                Réclamez cette fiche gratuitement en 2 clics et activez votre profil partenaire pour apparaître en tête de l'annuaire.
              </p>
              <button onClick={claim} className="w-full bg-white text-primary hover:bg-[#F9F8F6] px-4 py-3 text-sm font-semibold rounded-sm transition-colors shadow-sm" data-testid="claim-btn">
                👉 C'est mon entreprise
              </button>
              <p className="text-xs opacity-75 mt-3 text-center">Gratuit · Connexion par simple email · Sans engagement</p>
            </div>
          )}

          {!isP && biz.contact_gated && (
            <div className="bg-card border border-border rounded-sm p-5" data-testid="masked-contact-card">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 shrink-0 rounded-full bg-accent grid place-items-center text-xl">🔒</div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Contact masqué</div>
                  <div className="font-display text-lg font-medium leading-snug">Téléphone et email visibles uniquement chez les partenaires Premium</div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                Vous pouvez envoyer un signal au commerçant — il sera prévenu qu'un client cherche à le joindre.
              </p>
              <button
                onClick={async () => {
                  try {
                    await api.post(`/public/contact-intent/${biz.slug}`, {});
                    toast.success("Votre demande a été envoyée au commerçant.");
                  } catch {
                    toast.error("Impossible d'envoyer la demande.");
                  }
                }}
                className="w-full bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-3 text-sm font-semibold rounded-sm transition-colors"
                data-testid="contact-intent-btn"
              >
                📣 Signaler mon intérêt au commerçant
              </button>
              <p className="text-[11px] text-muted-foreground text-center mt-3">Le commerçant reçoit un email anonymisé · Aucun compte requis</p>
            </div>
          )}

          {!isP && <PremiumComparator businessName={biz.name} compact />}

          {!isP && <LaunchOfferBanner businessName={biz.name} onClaim={claim} />}

          <form onSubmit={sendContact} className="bg-card border border-border rounded-sm p-6 grid gap-3" data-testid="contact-form">
            <div className="font-display font-semibold flex items-center gap-2"><EnvelopeSimple size={18}/> {t.business.contact_form}</div>
            <input required value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} placeholder={t.business.name} className="border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary" data-testid="contact-name"/>
            <input required type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} placeholder={t.business.email} className="border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary" data-testid="contact-email"/>
            <input value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} placeholder={t.business.phone} type="tel" inputMode="tel" autoComplete="tel" className="border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary" data-testid="contact-phone"/>
            <textarea required value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} placeholder={t.business.message} rows={4} className="border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary" data-testid="contact-message"/>
            <button type="submit" disabled={sending} className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-2.5 text-sm font-semibold rounded-sm disabled:opacity-50" data-testid="contact-submit">{sending ? "…" : t.business.send_message}</button>
          </form>
        </aside>
      </div>
      <QuoteRequestModal business={biz} isOpen={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </div>
  );
}
